import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import Sidebar from "@/components/Sidebar";
import Spreadsheet from "@/components/Spreadsheet";
import BreakdownPanel from "@/components/BreakdownPanel";
import {
  loadTemplates,
  saveTemplates,
  loadActiveTemplateId,
  saveActiveTemplateId,
  createDefaultTemplate,
  createDefaultProduct,
  genId,
} from "@/lib/storage";
import { fetchRates } from "@/lib/fx";
import { exportCSV, exportXLSX, exportPDF } from "@/lib/exports";

export default function Calculator() {
  const [templates, setTemplates] = useState(() => {
    let tmps = loadTemplates();
    if (tmps.length === 0) {
      const seed = createDefaultTemplate("Product 1");
      seed.inputCurrency = "GBP";
      seed.displayCurrency = "GBP";
      seed.products = [
        { id: genId(), name: "Toy Bus", cost: 2, quantity: 100 },
      ];
      tmps = [seed];
      saveTemplates(tmps);
      saveActiveTemplateId(seed.id);
    }
    return tmps;
  });
  const [activeTemplateId, setActiveTemplateId] = useState(() => {
    const tmps = loadTemplates();
    return loadActiveTemplateId() || tmps[0]?.id || null;
  });
  const [activeProductId, setActiveProductId] = useState(() => {
    const tmps = loadTemplates();
    const activeId = loadActiveTemplateId() || tmps[0]?.id;
    const active = tmps.find((t) => t.id === activeId) || tmps[0];
    return active?.products?.[0]?.id || null;
  });
  const [ratesObj, setRatesObj] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);

  const template = useMemo(
    () => templates.find((t) => t.id === activeTemplateId) || null,
    [templates, activeTemplateId]
  );

  // Fetch FX rates whenever input currency changes (rates are based on input ccy)
  const loadFX = useCallback(
    async (base) => {
      if (!base) return;
      setFxLoading(true);
      try {
        const r = await fetchRates(base);
        setRatesObj(r);
      } catch (e) {
        toast.error("Could not load live FX rates. Using 1:1 fallback.");
        setRatesObj({ base, date: "—", rates: { [base]: 1 }, fetchedAt: Date.now() });
      } finally {
        setFxLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (template?.inputCurrency) {
      loadFX(template.inputCurrency);
    }
  }, [template?.inputCurrency, loadFX]);

  const persistTemplates = (next) => {
    setTemplates(next);
    saveTemplates(next);
  };

  const updateTemplate = (updated) => {
    const next = templates.map((t) =>
      t.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : t
    );
    persistTemplates(next);
  };

  // Template ops
  const handleNewTemplate = () => {
    const t = createDefaultTemplate(`Template ${templates.length + 1}`);
    const next = [...templates, t];
    persistTemplates(next);
    setActiveTemplateId(t.id);
    saveActiveTemplateId(t.id);
    setActiveProductId(t.products[0].id);
    toast.success("New template created");
  };

  const handleSelectTemplate = (id) => {
    setActiveTemplateId(id);
    saveActiveTemplateId(id);
    const t = templates.find((x) => x.id === id);
    setActiveProductId(t?.products?.[0]?.id || null);
  };

  const handleDuplicateTemplate = () => {
    if (!template) return;
    const copy = {
      ...JSON.parse(JSON.stringify(template)),
      id: genId(),
      name: `${template.name} (copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    // Regenerate product IDs to keep them unique
    copy.products = copy.products.map((p) => ({ ...p, id: genId() }));
    const next = [...templates, copy];
    persistTemplates(next);
    setActiveTemplateId(copy.id);
    saveActiveTemplateId(copy.id);
    setActiveProductId(copy.products[0]?.id || null);
    toast.success("Template duplicated");
  };

  const handleDeleteTemplate = () => {
    if (!template) return;
    const next = templates.filter((t) => t.id !== template.id);
    persistTemplates(next);
    if (next.length === 0) {
      const t = createDefaultTemplate("New Template");
      persistTemplates([t]);
      setActiveTemplateId(t.id);
      saveActiveTemplateId(t.id);
      setActiveProductId(t.products[0]?.id || null);
    } else {
      setActiveTemplateId(next[0].id);
      saveActiveTemplateId(next[0].id);
      setActiveProductId(next[0].products?.[0]?.id || null);
    }
    toast.success("Template deleted");
  };

  // Cost lines ops
  const handleAddCostLine = (label) => {
    if (!template) return;
    const newLine = {
      id: genId(),
      label,
      type: "percent",
      value: 0,
      base: "running",
    };
    updateTemplate({ ...template, costLines: [...template.costLines, newLine] });
  };

  const handleUpdateCostLine = (idx, line) => {
    if (!template) return;
    const next = [...template.costLines];
    next[idx] = line;
    updateTemplate({ ...template, costLines: next });
  };

  const handleRemoveCostLine = (idx) => {
    if (!template) return;
    const next = template.costLines.filter((_, i) => i !== idx);
    updateTemplate({ ...template, costLines: next });
  };

  // Product ops
  const handleUpdateProduct = (id, updated) => {
    if (!template) return;
    const products = template.products.map((p) => (p.id === id ? updated : p));
    updateTemplate({ ...template, products });
  };

  const handleAddProduct = () => {
    if (!template) return;
    const p = createDefaultProduct();
    const products = [...template.products, p];
    updateTemplate({ ...template, products });
    setActiveProductId(p.id);
  };

  const handleRemoveProduct = (id) => {
    if (!template) return;
    const products = template.products.filter((p) => p.id !== id);
    updateTemplate({ ...template, products });
    if (activeProductId === id) {
      setActiveProductId(products[0]?.id || null);
    }
  };

  // Save (toast confirmation; everything autosaves to localStorage but explicit save reassures)
  const handleSave = () => {
    if (!template) return;
    updateTemplate(template);
    toast.success(`"${template.name}" saved locally`);
  };

  const safeExport = (fn) => {
    if (!template) return;
    if (!template.products || template.products.length === 0) {
      toast.error("Add at least one product first");
      return;
    }
    try {
      fn(template, ratesObj);
      toast.success("Export started");
    } catch (e) {
      toast.error("Export failed");
      console.error(e);
    }
  };

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500 font-mono text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
      <TopBar
        templateName={template.name}
        onTemplateNameChange={(v) => updateTemplate({ ...template, name: v })}
        onSave={handleSave}
        onExportCSV={() => safeExport(exportCSV)}
        onExportXLSX={() => safeExport(exportXLSX)}
        onExportPDF={() => safeExport(exportPDF)}
        onRefreshFX={() => loadFX(template.inputCurrency)}
        fxDate={ratesObj?.date}
        fxLoading={fxLoading}
      />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          templates={templates}
          activeTemplateId={activeTemplateId}
          onSelectTemplate={handleSelectTemplate}
          onNewTemplate={handleNewTemplate}
          onDuplicateTemplate={handleDuplicateTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          template={template}
          onUpdateTemplate={updateTemplate}
          onAddCostLine={handleAddCostLine}
          onUpdateCostLine={handleUpdateCostLine}
          onRemoveCostLine={handleRemoveCostLine}
        />

        <main
          className="flex-1 flex flex-col overflow-hidden"
          style={{ height: "calc(100vh - 60px)" }}
        >
          <Spreadsheet
            template={template}
            ratesObj={ratesObj}
            activeProductId={activeProductId}
            onActivateProduct={setActiveProductId}
            onUpdateProduct={handleUpdateProduct}
            onAddProduct={handleAddProduct}
            onRemoveProduct={handleRemoveProduct}
          />
        </main>

        <BreakdownPanel
          template={template}
          ratesObj={ratesObj}
          activeProductId={activeProductId}
        />
      </div>
    </div>
  );
}
